import React, { useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Tabs,
    Tab,
    Snackbar,
    Alert as MuiAlert,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Grow, // Import Grow for transitions
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { ExportButton } from "./planVsActualExportButton";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function TableGenerate({
    selectedShiftRows,
    selectedOperationRows,
    shiftOutDatetime,
    machineCode,
    shiftStartDatetime,
    selectedDate,
}) {
    const [activeTab, setActiveTab] = useState(0);
    const [editableShiftRows, setEditableShiftRows] = useState(
        selectedShiftRows.map((row, index) => ({ id: index + 1, ...row }))
    );
    const [editableOperationRows, setEditableOperationRows] = useState(
        selectedOperationRows.map((row, index) => ({ id: index + 1, ...row }))
    );
    const [mainFetchedData, setMainFetchedData] = useState([]);
    const [opelogFetchedData, setOpelogFetchedData] = useState([]);
    const [shiftsFetchedData, setShiftsFetchedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbars, setSnackbars] = useState([]);
    const [isDataFetched, setIsDataFetched] = useState(false); // Track if data fetch button is clicked

    const enqueueSnackbar = (type, message) => {
        setSnackbars((prev) => [...prev, { type, message }]);
    };

    const handleSnackbarClose = (index) => {
        setSnackbars((prev) => prev.filter((_, i) => i !== index));
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const deleteExistingData = async () => {
        setLoading(true);
        try {
            const formattedShiftOutDatetime = dayjs(shiftOutDatetime).format("YYYY-MM-DD");
            const response = await fetch(`${API_BASE_URL}/deleteplanvsactual`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    machineCode,
                    endDate: formattedShiftOutDatetime,
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to delete data");
            }

            const data = await response.json();
            enqueueSnackbar("success", data.message || "Data deleted successfully");
            setMainFetchedData([]);
            setOpelogFetchedData([]);
            setShiftsFetchedData([]);
        } catch (error) {
            enqueueSnackbar("error", "Failed to delete data");
            console.error("Error deleting data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchExistingData = async () => {
        setLoading(true);
        setIsDataFetched(true); // Set the state to true when the button is clicked
        try {
            const formattedShiftOutDatetime = dayjs(shiftOutDatetime).format("YYYY-MM-DD");
            const response = await fetch(
                `${API_BASE_URL}/getplanvsactual?machineCode=${machineCode}&endDate=${formattedShiftOutDatetime}`
            );

            if (!response.ok) {
                throw new Error("Failed to fetch existing data");
            }
            const data = await response.json();
            setOpelogFetchedData(Array.isArray(data.opelog) ? data.opelog : []);
            setMainFetchedData(Array.isArray(data.main) ? data.main : []);
            setShiftsFetchedData(Array.isArray(data.shifts) ? data.shifts : []);
            enqueueSnackbar("success", "Fetched existing data successfully");
        } catch (error) {
            enqueueSnackbar("error", "Failed to fetch data");
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const generateNewData = async () => {
        setLoading(true);
        try {
            const formattedShiftStartDatetime = dayjs(shiftStartDatetime).format("YYYY-MM-DD HH:mm:ss");
            const formattedShiftOutDateTime = dayjs(shiftOutDatetime).format("YYYY-MM-DD HH:mm:ss");

            const payload = {
                machineCode,
                shiftStartDatetime: formattedShiftStartDatetime,
                shiftOutDatetime: formattedShiftOutDateTime,
                editableShiftRows,
                editableOperationRows: editableOperationRows.length > 0 ? editableOperationRows : [],
            };

            console.log("Payload being sent to the backend:", payload);

            const response = await fetch(`${API_BASE_URL}/generateplanvsactual`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Failed to generate new data");
            }

            const data = await response.json();
            enqueueSnackbar("success", "Data generated successfully");
            console.log("Generated Data from Backend:", data);
            setMainFetchedData(data.results.planvsactual || []);
            setOpelogFetchedData(data.results.planvsactualopelog || []);
            setShiftsFetchedData(data.results.planvsactualshifts || []);
        } catch (error) {
            enqueueSnackbar("error", "Failed to generate data");
            console.error("Error generating data:", error);
        } finally {
            setLoading(false);
        }
    };

    const shiftColumns = [
        { field: "Employee_ID", headerName: "Employee ID", width: 150, editable: true },
        {
            field: "shift_loggedIn_DateTime",
            headerName: "Logged In",
            width: 200,
            valueFormatter: (params) => {
                if (!params) {
                    return "";
                }
                return dayjs(params).format("YYYY-MM-DD HH:mm A");
            },
        },
        {
            field: "shift_loggedOut_DateTime",
            headerName: "Logged Out",
            width: 200,
            valueFormatter: (params) => {
                if (!params) {
                    return "N/A";
                }
                return dayjs(params).format("YYYY-MM-DD HH:mm A");
            },
        },
        { field: "operator", headerName: "Operator", width: 200, editable: true },
        { field: "AstOperator", headerName: "Assistant Operator", width: 200, editable: true },
        { field: "Helpers", headerName: "Helpers", width: 200, editable: true },
        { field: "remarks", headerName: "Remarks", width: 300, editable: true },
    ];

    const operationcolumns = [
        { field: "employee_id", headerName: "Employee ID", width: 150, editable: true },
        { field: "name", headerName: "Name", width: 150, editable: true },
        { field: "operation_code", headerName: "Operation Code", width: 150, editable: true },
        { field: "operation_description", headerName: "Operation Description", width: 250, editable: true },
        {
            field: "start_datetime", headerName: "Start Datetime", width: 200,
            valueFormatter: (params) => {
                if (!params) {
                    return "N/A";
                }
                return dayjs(params).format("YYYY-MM-DD HH:mm A");
            },
        },
        {
            field: "end_datetime", headerName: "End Datetime", width: 200,
            valueFormatter: (params) => {
                if (!params) {
                    return "N/A";
                }
                return dayjs(params).format("YYYY-MM-DD HH:mm A");
            },
        },
        { field: "remarks", headerName: "Remarks", width: 300, editable: true },
    ];

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Shift/Operation Log Edit and Confirm" />
                    <Tab label="Generate Plan vs Actual" />
                </Tabs>

                {activeTab === 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Edit Shift Logs
                            </Typography>
                            <DataGrid
                                rows={editableShiftRows}
                                columns={shiftColumns}
                                pageSize={5}
                                rowsPerPageOptions={[5, 10, 20]}
                                autoHeight
                                onEditCellChangeCommitted={(params) => {
                                    const updatedRows = [...editableShiftRows];
                                    const index = updatedRows.findIndex((row) => row.id === params.id);
                                    if (index !== -1) {
                                        updatedRows[index] = { ...updatedRows[index], [params.field]: params.value };
                                        setEditableShiftRows(updatedRows);
                                    }
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Edit Operation Logs
                            </Typography>
                            <DataGrid
                                rows={editableOperationRows}
                                columns={operationcolumns}
                                pageSize={5}
                                rowsPerPageOptions={[5, 10, 20]}
                                autoHeight
                                onEditCellChangeCommitted={(params) => {
                                    const updatedRows = [...editableOperationRows];
                                    const index = updatedRows.findIndex((row) => row.id === params.id);
                                    if (index !== -1) {
                                        updatedRows[index] = { ...updatedRows[index], [params.field]: params.value };
                                        setEditableOperationRows(updatedRows);
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Generate Plan vs Actual
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={fetchExistingData}
                                disabled={loading}
                            >
                                {loading ? "Fetching..." : "View Existing Data"}
                            </Button>
                            {(mainFetchedData.length > 0 || opelogFetchedData.length > 0 || shiftsFetchedData.length > 0) ? (
                                <>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={deleteExistingData}
                                        disabled={loading}
                                    >
                                        {loading ? "Deleting..." : "Delete Existing Data"}
                                    </Button>
                                    <ExportButton
                                        shiftsFetchedData={shiftsFetchedData}
                                        mainFetchedData={mainFetchedData}
                                        opelogFetchedData={opelogFetchedData}
                                    />
                                </>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={generateNewData}
                                    disabled={loading}
                                >
                                    {loading ? "Generating..." : "Generate New Data"}
                                </Button>
                            )}
                        </Box>

                        {/* Main Data Table */}
                        {mainFetchedData.length > 0 ? (
                            <Grow in={true} timeout={500}>
                                <Box sx={{ width: "100%", overflow: "auto", maxHeight: 400, mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>Main Data</Typography>
                                    <TableContainer sx={{ overflowX: "auto" }}>
                                        <Table aria-label="Main Data Table" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {Object.keys(mainFetchedData[0]).map((key) => (
                                                        <TableCell key={key} sx={{ minWidth: 150, whiteSpace: "nowrap" }}>
                                                            {key}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {mainFetchedData.map((row, rowIndex) => (
                                                    <TableRow key={rowIndex}>
                                                        {Object.entries(row).map(([key, value], cellIndex) => (
                                                            <TableCell key={cellIndex} sx={{ minWidth: 150, whiteSpace: "nowrap" }}>
                                                                {key === "FilterEndDate"
                                                                    ? dayjs(value).format("YYYY-MM-DD") // Show Date Only
                                                                    : key === "created_at" || key === "updated_at"
                                                                        ? dayjs(value).format("YYYY-MM-DD HH:mm A") // Show Date & Time
                                                                        : value ?? "N/A"}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Grow>
                        ) : isDataFetched && (
                            <Grow in={true} timeout={800}>
                                <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mt: 2, textAlign: "center" }}>
                                    <Typography variant="body1" sx={{ mt: 2, textAlign: "center" }}>
                                        No plan vs Actual data available for the selected criteria.
                                    </Typography>
                                </Box>
                            </Grow>
                        )}


                        {/* Operation Logs Table */}
                        {Array.isArray(opelogFetchedData) && opelogFetchedData.length > 0 ? (
                            <Grow in={true} timeout={800}>
                                <Box sx={{ width: "100%", overflow: "auto", maxHeight: 400, mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>Operation Logs</Typography>
                                    <TableContainer sx={{ overflowX: "auto" }}>
                                        <Table aria-label="Operation Logs Table" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {opelogFetchedData.length > 0 && Object.keys(opelogFetchedData[0]).map((key) => (
                                                        <TableCell key={key} sx={{ minWidth: 150, whiteSpace: "nowrap" }}>
                                                            {key}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {opelogFetchedData.map((row, rowIndex) => (
                                                    <TableRow key={rowIndex}>
                                                        {Object.entries(row).map(([key, value], cellIndex) => (
                                                            <TableCell key={cellIndex} sx={{ minWidth: 150, whiteSpace: "nowrap" }}>
                                                                {key === "planvsactualDate"
                                                                    ? dayjs(value).format("YYYY-MM-DD")
                                                                    : key.includes("datetime") || key.includes("createdat") || key.includes("lastupdate")
                                                                        ? dayjs(value).format("YYYY-MM-DD HH:mm A")
                                                                        : value ?? "N/A"}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Grow>
                        ) : isDataFetched && (
                            <Grow in={true} timeout={800}>
                                <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mt: 2, textAlign: "center" }}>
                                    <Typography variant="body1">
                                        No operation logs available for the selected criteria.
                                    </Typography>
                                </Box>
                            </Grow>
                        )}

                        {/* Shift Logs Table */}
                        {shiftsFetchedData.length > 0 ? (
                            <Grow in={true} timeout={1100}>
                                <Box sx={{ width: "100%", overflow: "auto", maxHeight: 400 }}>
                                    <Typography variant="h6" gutterBottom>Shift Logs</Typography>
                                    <TableContainer sx={{ overflowX: "auto" }}>
                                        <Table aria-label="Shift Logs Table" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {Object.keys(shiftsFetchedData[0]).map((key) => (
                                                        <TableCell key={key} sx={{ minWidth: 150, whiteSpace: "nowrap" }}>
                                                            {key}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {shiftsFetchedData.map((row, rowIndex) => (
                                                    <TableRow key={rowIndex}>
                                                        {Object.entries(row).map(([key, value], cellIndex) => (
                                                            <TableCell key={cellIndex} sx={{ minWidth: 150, whiteSpace: "nowrap" }}>
                                                                {key === "planvsactualDate"
                                                                    ? dayjs(value).format("YYYY-MM-DD")
                                                                    : key.includes("datetime") || key.includes("createdat") || key.includes("lastupdate") || key.includes("shift_loggedIn_DateTime") || key.includes("shift_loggedOut_DateTime")
                                                                        ? dayjs(value).format("YYYY-MM-DD HH:mm A")
                                                                        : value ?? "N/A"}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Grow>
                        ) : isDataFetched && (
                            <Grow in={true} timeout={1100}>
                                <Box sx={{ backgroundColor: '#f3e5f5', p: 2, borderRadius: 1, mt: 2, textAlign: "center" }}>
                                    <Typography variant="body1">
                                        No shift logs available for the selected criteria.
                                    </Typography>
                                </Box>
                            </Grow>
                        )}
                    </Box>
                )}

                {snackbars.map((snackbar, index) => (
                    <Snackbar
                        key={index}
                        open
                        autoHideDuration={5000}
                        onClose={() => handleSnackbarClose(index)}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <MuiAlert
                            onClose={() => handleSnackbarClose(index)}
                            severity={snackbar.type}
                            sx={{ width: "100%" }}
                        >
                            {snackbar.message}
                        </MuiAlert>
                    </Snackbar>
                ))}
            </CardContent>
        </Card>
    );
}